import { getSessionToken } from '@shopify/app-bridge/utilities';

// Check if authentication is disabled for development
const AUTH_DISABLED = import.meta.env.VITE_DISABLE_SHOPIFY_AUTH === 'true';

// CRITICAL: App runs in Shopify iframe, MUST use absolute URLs
// Relative paths would resolve to admin.shopify.com instead of your app domain
if (!import.meta.env.VITE_API_URL) {
  throw new Error(
    'VITE_API_URL is required! ' +
    'App runs in Shopify iframe and needs absolute URLs. ' +
    'Set VITE_API_URL in .env file.'
  );
}

const API_URL = `${import.meta.env.VITE_API_URL}/api`;

// Store App Bridge instance globally (set by App.jsx)
let appBridgeInstance = null;

export function setAppBridge(app) {
  appBridgeInstance = app;
}

/**
 * Authenticated fetch wrapper that includes session token from App Bridge
 */
async function authenticatedFetch(url, options = {}) {
  // Skip App Bridge in dev mode if authentication is disabled
  if (AUTH_DISABLED) {
    console.warn('⚠️  App Bridge authentication is DISABLED (development mode)');
    return fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      credentials: 'include'
    });
  }

  if (!appBridgeInstance) {
    console.warn('App Bridge not initialized, making unauthenticated request');
    return fetch(url, options);
  }

  try {
    // Get fresh session token from App Bridge
    const token = await getSessionToken(appBridgeInstance);

    // Prepare headers with session token
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
      'Authorization': `Bearer ${token}`
    };

    // Make the request
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include'
    });

    // Handle 401 responses (App Bridge will auto-retry with fresh token via X-Shopify-Retry header)
    if (response.status === 401) {
      const retryHeader = response.headers.get('X-Shopify-Retry-Invalid-Session-Request');
      if (retryHeader) {
        // App Bridge will handle retry automatically
        console.log('Session token expired, App Bridge will retry');
      }
    }

    return response;
  } catch (error) {
    console.error('Authenticated fetch error:', error);
    throw error;
  }
}

/**
 * API wrapper with axios-like interface
 */
const api = {
  async get(endpoint, config = {}) {
    const url = `${API_URL}${endpoint}`;
    const queryString = config.params ? '?' + new URLSearchParams(config.params).toString() : '';
    const response = await authenticatedFetch(`${url}${queryString}`, {
      method: 'GET',
      ...config
    });
    
    // Handle blob responses (file downloads)
    if (config.responseType === 'blob') {
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      // Return blob data with headers (for Content-Disposition filename)
      const blob = await response.blob();
      const headers = {};
      response.headers.forEach((value, key) => {
        headers[key] = value;
      });
      return { data: blob, headers };
    }
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `Request failed with status ${response.status}`);
    }
    
    return { data: await response.json() };
  },

  async post(endpoint, data, config = {}) {
    const url = `${API_URL}${endpoint}`;
    const isFormData = data instanceof FormData;
    
    const response = await authenticatedFetch(url, {
      method: 'POST',
      body: isFormData ? data : JSON.stringify(data),
      headers: isFormData ? {} : { 'Content-Type': 'application/json' },
      ...config
    });

    // Handle blob responses (file downloads)
    if (config.responseType === 'blob') {
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      // Return blob data with headers (for Content-Disposition filename)
      const blob = await response.blob();
      const headers = {};
      response.headers.forEach((value, key) => {
        headers[key] = value;
      });
      return { data: blob, headers };
    }
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `Request failed with status ${response.status}`);
    }
    
    return { data: await response.json() };
  },

  async put(endpoint, data, config = {}) {
    const url = `${API_URL}${endpoint}`;
    const response = await authenticatedFetch(url, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...config
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `Request failed with status ${response.status}`);
    }
    
    return { data: await response.json() };
  },

  async delete(endpoint, config = {}) {
    const url = `${API_URL}${endpoint}`;
    const response = await authenticatedFetch(url, {
      method: 'DELETE',
      ...config
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `Request failed with status ${response.status}`);
    }
    
    return { data: await response.json() };
  }
};

// ... rest of exports remain the same
export const dashboardAPI = {
  getSummary: (params) => api.get('/dashboard/summary', { params }),
  getVendorMetrics: (params) => api.get('/dashboard/vendor-metrics', { params }),
  getCharts: (params) => api.get('/dashboard/charts', { params })
};

export const vendorAPI = {
  getAll: (params) => api.get('/vendors', { params }),
  getById: (id) => api.get(`/vendors/${id}`),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
  getTemplates: (vendorId) => api.get(`/vendors/${vendorId}/templates`)
};

export const templateAPI = {
  getVendorTemplate: (vendorId) => api.get(`/vendors/${vendorId}/template`),
  createTemplate: (vendorId, data) => api.post(`/vendors/${vendorId}/template`, data),
  updateTemplate: (vendorId, templateId, data) => api.put(`/vendors/${vendorId}/template/${templateId}`, data),
  previewTemplate: (vendorId, data) => api.post(`/vendors/${vendorId}/template/preview`, data),
  testSnippet: (vendorId, data) => api.post(`/vendors/${vendorId}/template/test-snippet`, data)
};

export const productMappingAPI = {
  getAll: (params) => api.get('/vendor-product-mappings', { params }),
  searchShopify: (q, limit = 20, cursor = null, mode = 'all') => api.get('/vendor-product-mappings/search-shopify', { params: { q, limit, cursor, mode } }),
  create: (data) => api.post('/vendor-product-mappings', data),
  update: (id, data) => api.put(`/vendor-product-mappings/${id}`, data),
  delete: (id) => api.delete(`/vendor-product-mappings/${id}`),
  bulkUpload: (formData) => api.post('/vendor-product-mappings/bulk-upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
};

export const orderAPI = {
  getPendingOrders: (params) => api.get('/pending-orders', { params }),
  getPendingOrdersByVendor: (params) => api.get('/pending-orders/vendors', { params }),
  exportVendorOrders: (data) => api.post('/pending-orders/export', data, { responseType: 'blob' }),
  exportAllVendorOrders: (data) => api.post('/pending-orders/export-all', data),
  getVendors: () => api.get('/vendors'),
  list: (params) => api.get('/orders/list', { params }),
  exportSelected: (data) => api.post('/orders/export-selected', data, { responseType: 'blob' })
};

export const syncAPI = {
  syncProducts: () => api.post('/sync/products'),
  syncOrders: (params) => api.post('/sync/orders', {}, { params })
};

export const exportHistoryAPI = {
  getAll: (params) => api.get('/export-history', { params }),
  getById: (id) => api.get(`/export-history/${id}`)
};

export const manualExportAPI = {
  getHistory: (params) => api.get('/manual-export/history', { params }),
  exportOrders: (data) => api.post('/manual-export/export', data, { responseType: 'blob' }),
  download: (exportId) => api.get(`/manual-export/download/${exportId}`, { responseType: 'blob' }),
  export: (data) => api.post('/manual-export', data, { responseType: 'blob' })
};

export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data)
};

export const webhookStatusAPI = {
  getStatus: () => api.get('/webhook-status/status')
};

export default api;